import { Data, Model, Utils } from '@singularsystems/neo-core';
import { AxiosPromise } from 'axios';
import { injectable } from 'inversify';
import { AppService, Types } from '../DomainTypes';
import PaymentExportPagedCriteria from '../Models/PaymentExport/Lookups/PaymentExportPagedCriteria';
import PaymentExportLookup from '../Models/PaymentExport/Lookups/PaymentExportLookup';
import PaymentExportDetailsPagedCriteria from '../Models/PaymentExport/Lookups/PaymentExportDetailsPagedCriteria';
import PaymentExportDetailLookup from '../Models/PaymentExport/Lookups/PaymentExportDetailLookup';
import NewPaymentExportCommand from '../Models/PaymentExport/Lookups/NewPaymentExportCommand';
import { PaymentType } from '../Models/PaymentExport/PaymentType';

export interface IPaymentExportApiClient {

    /**
     * Gets the next recommended PaymentExport start date criteria
     * @param paymentType The payment export type
     * @returns The date of the next suggested payment export
     */
    getNextSuggestedPaymentExportStartDate(paymentType: PaymentType): AxiosPromise<string>;

    /**
     * Gets a paged list of PaymentExportLookups
     */
    getPaymentExportsPaged(request: Model.PartialPlainNonTrackedObject<Data.PageRequest<PaymentExportPagedCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<PaymentExportLookup>>>;

    /** 
     * Fetches a payment export record.
     */
    getPaymentExportLookup(paymentExportId: number): AxiosPromise<Model.PlainObject<PaymentExportLookup>>;

    /**
     * Gets a paged list of PaymentExportDetails
     */
    getPaymentExportDetailsPaged(request: Model.PartialPlainNonTrackedObject<Data.PageRequest<PaymentExportDetailsPagedCriteria>>): AxiosPromise<Data.PageResult<Model.PlainTrackedObject<PaymentExportDetailLookup>>>;

    /**
     * Creates a PaymentExport and the corresponding PaymentExportDetails
     * @param command The command for creating a PaymentExport and the associated PaymentExportDetails
     * @returns A NewPaymentExportLookup containing the lookup of the export and a page result of the details
     */
    saveNewPaymentExport(command: Model.PartialPlainObject<NewPaymentExportCommand>): AxiosPromise<Model.PlainObject<PaymentExportLookup>[]>;

    /**
     * Deletes a PaymentExport and all linked PaymentExportDetails
     * @param paymentExportId The ID of the PaymentExport to delete
     * @returns A Task
     */
    deletePaymentExport(paymentExportId: number): AxiosPromise;

    /**
     * Deletes a PaymentExportDetail from a PaymentExport batch
     * @param paymentExportDetailId The ID of the PaymentExportDetail to delete
     * @returns A Task
     */
    deletePaymentExportDetail(paymentExportDetailId: number): AxiosPromise;

    /** 
     * Exports a PaymentExport to a CSV file
     * @param paymentExportId The ID of the PaymentExport to export
     * @returns A  containing the generated CSV file
     */
    export(paymentExportId: number): AxiosPromise<Blob>;

    // Client only properties / methods
}

@injectable()
export default class PaymentExportApiClient extends Data.ApiClientBase implements IPaymentExportApiClient {

    constructor (config = AppService.get(Types.App.Config)) {
        super(`${config.apiPath}/payment-export`);
    }

    public getNextSuggestedPaymentExportStartDate(paymentType: PaymentType): AxiosPromise<string> {
        return this.axios.get(`${this.apiPath}/next-export-date?paymentType=${paymentType}`);
    }

    public getPaymentExportsPaged(request: Model.PartialPlainNonTrackedObject<Data.PageRequest<PaymentExportPagedCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<PaymentExportLookup>>> {
        return this.axios.get(`${this.apiPath}/paged?${Utils.getQueryString(request)}`);
    }

    public getPaymentExportLookup(paymentExportId: number): AxiosPromise<Model.PlainObject<PaymentExportLookup>> {
        return this.axios.get(`${this.apiPath}/${paymentExportId}`);
    }

    public getPaymentExportDetailsPaged(request: Model.PartialPlainNonTrackedObject<Data.PageRequest<PaymentExportDetailsPagedCriteria>>): AxiosPromise<Data.PageResult<Model.PlainTrackedObject<PaymentExportDetailLookup>>> {
        return this.axios.get(`${this.apiPath}/details-paged?${Utils.getQueryString(request)}`);
    }

    public saveNewPaymentExport(command: Model.PartialPlainObject<NewPaymentExportCommand>): AxiosPromise<Model.PlainObject<PaymentExportLookup>[]> {
        return this.axios.post(`${this.apiPath}/save-payment-export`, command);
    }

    public deletePaymentExport(paymentExportId: number): AxiosPromise {
        return this.axios.post(`${this.apiPath}/delete/${paymentExportId}`);
    }

    public deletePaymentExportDetail(paymentExportDetailId: number): AxiosPromise {
        return this.axios.post(`${this.apiPath}/delete-detail/${paymentExportDetailId}`);
    }

    public export(paymentExportId: number): AxiosPromise<Blob> {
        return this.axios.post(`${this.apiPath}/export/${paymentExportId}`, null, { responseType: "blob" });
    }

    // Client only properties / methods
}